import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface TipoDespesasVendedorType {
    id: number;
    name: string;
    icon?: string;
}

export interface CreateTipoDespesasVendedorType {
    name: string
    icon?: string;
    empresaId: number;
}

export interface UpdateTipoDespesasVendedorType {
    id: number;
    name: string;
    icon?: string;
}

export default function useTipoDespesasVendedor() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listTipoDespesasVendedor, setListTipoDespesasVendedor] = useState<TipoDespesasVendedorType[]>([]);
    const [listTipoDespesasVendedorByEmpresa, setListTipoDespesasVendedorByEmpresa] = useState<TipoDespesasVendedorType[]>([]);
    const [tipoDespesasVendedorPageCount, setTipoDespesasVendedorPageCount] = useState<number>(0);
    const [initialListTipoDespesasVendedor, setInitialListTipoDespesasVendedor] = useState<TipoDespesasVendedorType[]>([]);
    const [tipoDespesasVendedor, setTipoDespesasVendedor] = useState<TipoDespesasVendedorType>({
        id: 0,
        name: "",
        icon: "",
    });

    function createTipoDespesasVendedor(createTipoDespesasVendedorType: CreateTipoDespesasVendedorType): Promise<CreateTipoDespesasVendedorType | null> {
        setLoading(true);
        return api
            .post(`/TipoDespesasVendedor/create`, createTipoDespesasVendedorType)
            .then((response) => {
                const createdTipoDespesasVendedor: CreateTipoDespesasVendedorType = response.data;
                showToast("success", "Tipo de despesa criada com sucesso.");
                setLoading(false);
                return createdTipoDespesasVendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar tipo de despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar tipo de depesa:", error);
                return null;
            });
    }

    function getAllTipoDespesasVendedorPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<TipoDespesasVendedorType[] | null> {
        setLoading(true);
        let url = `/TipoDespesasVendedor/getAllTipoDespesasVendedorPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListTipoDespesasVendedor(response.data.result);
                setInitialListTipoDespesasVendedor(response.data.result);
                setTipoDespesasVendedorPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar tipos de depesas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar tipos de despesas:", error);
                return null;
            });
    }

    function updateTipoDespesasVendedor(updateTipoDespesasVendedorData: UpdateTipoDespesasVendedorType): Promise<UpdateTipoDespesasVendedorType | null> {
        setLoading(true);
        return api
            .put(`/TipoDespesasVendedor/${updateTipoDespesasVendedorData.id}`, updateTipoDespesasVendedorData)
            .then((response) => {
                showToast("success", "Tipo de despesa atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar tipo de despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar tipo de despesa:", error);
                return null;
            });
    }

    function deleteTipoDespesasVendedor(tipoDespesasVendedorId: number): Promise<TipoDespesasVendedorType | null> {
        setLoading(true);
        return api
            .delete(`/TipoDespesasVendedor/${tipoDespesasVendedorId}`)
            .then(() => {
                setTipoDespesasVendedor({
                    id: 0,
                    name: "",
                    icon: "",
                });
                showToast("success", "Tipo de despesa deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar tipo de despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar tipo de despesa:", error);
                return null;
            });
    }

    function handleGetTipoDespesasVendedorByEmpresaId(empresaId: number): Promise<TipoDespesasVendedorType[] | null> {
        setLoading(true);
        return api
            .get(`/TipoDespesasVendedor/getTipoDespesasVendedorByEmpresaId/${empresaId}`)
            .then((response) => {
                const tipoDespesasVendedor: TipoDespesasVendedorType[] = response.data;
                setListTipoDespesasVendedorByEmpresa(tipoDespesasVendedor);
                setLoading(false);
                return tipoDespesasVendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações dos tipos de despesas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações dos tipos de despesas:", error);
                return null;
            });
    }

    return {
        createTipoDespesasVendedor,
        getAllTipoDespesasVendedorPaged,
        updateTipoDespesasVendedor,
        deleteTipoDespesasVendedor,
        setListTipoDespesasVendedor,
        listTipoDespesasVendedor,
        setTipoDespesasVendedorPageCount,
        tipoDespesasVendedorPageCount,
        setInitialListTipoDespesasVendedor,
        initialListTipoDespesasVendedor,
        setTipoDespesasVendedor,
        tipoDespesasVendedor,
        handleGetTipoDespesasVendedorByEmpresaId,
        setListTipoDespesasVendedorByEmpresa,
        listTipoDespesasVendedorByEmpresa,
    };
}